import * as React from 'react';
import { GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';
import { roundedRect, measureTextWidth, BOX_HEIGHT, BOX_RADIUS, FONT_NORMAL, FONT_BOLD } from '@/utils/canvasUtils';
import { COLORS } from '@/constants/colors';

// ─── 삼각형 아이콘 (▼) ──────────────────────────────────────────
function drawTriangle(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx - size / 2, cy - size / 3);
  ctx.lineTo(cx + size / 2, cy - size / 3);
  ctx.lineTo(cx, cy + size / 2);
  ctx.closePath();
  ctx.fill();
}

// ─── 레이아웃 상수 ────────────────────────────────────────────────
const RADIUS = BOX_RADIUS;
const GAP = 6; // 요소 간격
const H_PAD = 8; // 전체 박스 좌우 내부 여백
const ITEM_PAD = 3; // 숫자 박스 상하 여백
const TEXT_PAD = 8; // 숫자 박스 좌우 여백
const TRIANGLE_SIZE = 8; // ▼ 아이콘 크기

const FONT = FONT_NORMAL;
const BOLD_FONT = FONT_BOLD;

// ─── Props ────────────────────────────────────────────────────────
export interface TPSLRatioBoxProps {
  readonly yValue: number;
  readonly color: string; // posColor (#F47515 헷지, #2EBD85 롱, #F6465D 숏)
  readonly ratioText: string; // 표시할 비율 텍스트 (e.g. "2.00", "TP/SL")
  readonly isDark: boolean;
  readonly rightOffset: number;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragAreaHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

/**
 * ctx 없이 렌더 전에 TPSLRatioBox 전체 너비를 계산한다.
 * 항상 'TP/SL' 텍스트 기준으로 계산해 고정 너비를 반환한다.
 * (숫자 표시 시에도 같은 너비 사용 → 레이아웃 흔들림 없음)
 */
export function calcRatioBoxWidth(): number {
  const labelW = measureTextWidth('TP/SL', FONT);
  const ratioTextW = measureTextWidth('TP/SL', BOLD_FONT);
  const ratioW = TEXT_PAD + ratioTextW + GAP + TRIANGLE_SIZE + TEXT_PAD;
  return H_PAD + labelW + GAP + ratioW + H_PAD;
}

export class TPSLRatioBox extends React.Component<TPSLRatioBoxProps> {
  static defaultProps = {
    isDark: false,
    rightOffset: 60,
  };

  private ratioX = 0;
  private ratioW = 0;
  private boxY = 0;
  public boxX = 0;

  render() {
    const { onHover, onUnHover, onClick, onDragAreaHover, onDragStart, onDrag, onDragComplete } = this.props;
    const hasDrag = !!(onDragStart || onDrag || onDragComplete);
    return (
      <>
        {/* 박스 렌더 + 드롭다운 hover/click: 숫자+삼각형 박스 영역만 히트 */}
        {/* clip={false}: ratio 박스가 yScale 범위를 벗어나도 그대로 표시 (핸들과 달리 서브차트 침범 우려 없음) */}
        <GenericChartComponent
          clip={false}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          isHover={this.isHover}
          drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
          onHover={onHover}
          onUnHover={onUnHover}
          onClick={onClick}
        />
        {/* 드래그 전용: 전체 박스 영역 히트, 그리기 없음 (clip={false}: 박스 렌더 컴포넌트와 동일 이유) */}
        {hasDrag && (
          <GenericChartComponent
            clip={false}
            canvasToDraw={getMouseCanvas}
            canvasDraw={this.drawNothing}
            isHover={this.isDragHover}
            enableDragOnHover
            interactiveCursorClass="react-financial-charts-ns-resize-cursor"
            drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
            onHover={onDragAreaHover ?? onHover}
            onUnHover={onDragAreaHover ?? onUnHover}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onDragComplete={onDragComplete}
          />
        )}
      </>
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    ctx.save();
    const { yValue, color, ratioText, isDark, rightOffset } = this.props;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;

    const y = Math.round(yScale(yValue));
    if (y < 0 || y > height) {
      ctx.restore();
      return;
    }

    const cy = y;

    // ─── 1. 너비 측정 ───────────────────────────────────────────
    ctx.font = FONT;
    const labelW = ctx.measureText('TP/SL').width;

    ctx.font = BOLD_FONT;
    // 숫자 박스: 좌측 TEXT_PAD + 숫자 + GAP + 삼각형 + 우측 TEXT_PAD
    const ratioTextW = ctx.measureText(ratioText).width;
    const ratioW = TEXT_PAD + ratioTextW + GAP + TRIANGLE_SIZE + TEXT_PAD;

    const totalW = H_PAD + labelW + GAP + ratioW + H_PAD;

    const boxX = width - totalW - rightOffset;
    const boxY = y - BOX_HEIGHT / 2;
    this.boxX = boxX;
    this.boxY = boxY;

    // ─── 2. x 좌표 계산 ─────────────────────────────────────────
    let cursor = boxX + H_PAD;
    const labelCx = cursor + labelW / 2;
    cursor += labelW + GAP;

    const ratioX = cursor;
    this.ratioX = ratioX;
    this.ratioW = ratioW;
    // 숫자 텍스트는 좌측 TEXT_PAD 이후 중앙
    const ratioTextCx = cursor + TEXT_PAD + ratioTextW / 2;
    // 삼각형은 숫자 이후 GAP + 삼각형 중앙
    const triangleCx = cursor + TEXT_PAD + ratioTextW + GAP + TRIANGLE_SIZE / 2;

    // ─── 3. 배경 & 테두리 ───────────────────────────────────────
    const bgColor = isDark ? COLORS.bgDark : COLORS.bgLight;
    ctx.fillStyle = bgColor;
    roundedRect(ctx, boxX, boxY, totalW, BOX_HEIGHT, RADIUS);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    roundedRect(ctx, boxX, boxY, totalW, BOX_HEIGHT, RADIUS);
    ctx.stroke();

    // 숫자+삼각형 박스: color 테두리
    roundedRect(ctx, ratioX, boxY + ITEM_PAD, ratioW, BOX_HEIGHT - ITEM_PAD * 2, RADIUS);
    ctx.stroke();

    // ─── 4. 텍스트 & 아이콘 ─────────────────────────────────────
    const textColor = color;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    // [TP/SL] 라벨
    ctx.font = FONT;
    ctx.fillText('TP/SL', labelCx, cy);

    // [비율 숫자] + [▼] (박스 안)
    ctx.font = BOLD_FONT;
    ctx.fillText(ratioText, ratioTextCx, cy);
    drawTriangle(ctx, triangleCx, cy, TRIANGLE_SIZE, textColor);

    ctx.restore();
  };

  // 드롭다운 hover/click: 숫자+삼각형 박스 영역만
  private readonly isHover = (moreProps: MoreProps) => {
    const { onHover, onClick } = this.props;
    if (!onHover && !onClick) return false;
    if (this.ratioW === 0) return false;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;
    return (
      mouseX >= this.ratioX &&
      mouseX <= this.ratioX + this.ratioW &&
      mouseY >= this.boxY &&
      mouseY <= this.boxY + BOX_HEIGHT
    );
  };

  // 드래그 전용: 전체 박스 영역
  private readonly isDragHover = (moreProps: MoreProps) => {
    if (this.ratioW === 0) return false;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;
    return (
      mouseX >= this.boxX &&
      mouseX <= this.ratioX + this.ratioW &&
      mouseY >= this.boxY &&
      mouseY <= this.boxY + BOX_HEIGHT
    );
  };

  private readonly drawNothing = () => {};
}
